import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import { createRoot } from 'react-dom/client';
import styled, { css, ThemeProvider } from 'styled-components';
import {
  Blocker,
  ConsumeWarningsUtil,
  DataClientProviderBridge,
  DataProvider,
  DesignSysEnvUtil,
  ErrorBoundary,
  ErrorLayout,
  LightTheme,
  LoggerUtil,
  PendoInitializer,
  ServiceWorkerProvider,
  Sidedrawer,
  Suspense,
  Toasts,
} from '@axiom/ui';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import themes from './themes';
import { EnvUtil } from './utils/env-util';
import { PendoProvider } from './components/PendoProvider/PendoProvider';
import { AppRouter } from './Router';
import { initializeStore } from './lib/store-manager';
import '@progress/kendo-theme-default/dist/all.css';
import '@axiom/ui/src/public/static/kendo-overrides.css';
// eslint-disable-next-line import/no-unresolved
import 'semantic-ui-less/semantic.less';
import '@axiom/ui/src/semantic-ui-components.less';
import '@axiom/ui/src/public/static/bootstrap.min.css';
import '@axiom/ui/src/public/static/main.css';
import '@axiom/ui/src/public/static/main-internal.css';
import '@axiom/ui/src/public/static/css-helpers.css';

/**
 * Temp disable until we can get to this ticket
 * APCORE-2484
 */
if (process.env.NODE_ENV !== 'production') {
  ConsumeWarningsUtil.consumePropWarnings();
}

const AppWrapper = styled.div`
  html {
    font-size: 1rem;
  }

  @media print {
    html {
      font-size: 1.25rem;
      padding-left: 0;
      padding-right: 0;
    }
    .row(.top-logo-header) {
      page-break-inside: avoid;
    }
  }

  hr {
    margin-top: 8px;
    margin-bottom: 0;
    background-color: rgb(0, 0, 0);
  }

  @page {
    margin-left: -50mm;
    margin-right: -50mm;
  }

  /**
    Temp solution till we tear out bootstrap and replace nav
   */
  @media screen and (min-width: 1024px) {
    .nav {
      padding: 0;
      display: flex !important;
      align-items: center;
    }
  }

  /**
    Temp solution till we tear out bootstrap and replace nav
   */
  @media (max-width: 1023px) {
    .axiom-nav {
      height: 100vh !important;
    }
  }

  .axiom-nav a {
    margin-right: 20px;
  }
`;

DesignSysEnvUtil.initialize({
  COOKIE_DOMAIN: EnvUtil.cookieDomain,
  JWT_COOKIE_KEY: EnvUtil.userCookieName,
  GOOGLE_MAPS_API_URL: EnvUtil.googleMapsApiUrl,
  LOGO_DEV_BASE_URL: EnvUtil.logoDevBaseUrl,
  LOGO_DEV_PUBLISHABLE_KEY: EnvUtil.logoDevPublishableKey,
  ENABLE_SUGGESTIONS: EnvUtil.enableSuggestions,
  S3_PUBLIC_BASE_URL: EnvUtil.publicBaseUrlS3,
  S3_PUBLIC_FILE_BUCKET: EnvUtil.publicS3Bucket,
  ENABLE_CALENDAR_EVENTS_TODAY: EnvUtil.enableCalendarEventsToday,
});

if (EnvUtil.enableLogging) {
  LoggerUtil.initialize(
    EnvUtil.logglyClientId,
    'client',
    EnvUtil.environmentName
  );
}

const store = initializeStore();

const Bootstrap = () => {
  const handleError = error => {
    const errorCode = error?.getStatusCode() ?? null;

    switch (errorCode) {
      case 401: {
        if (window.location.pathname !== '/401') {
          window.location.href = '/401';
        }
        break;
      }
      case 403: {
        if (window.location.pathname !== '/403') {
          window.location.href = '/403';
        }
        break;
      }
      case 404: {
        if (window.location.pathname !== '/404') {
          window.location.href = '/404';
        }
        break;
      }
      default: {
        break;
      }
    }

    return <ErrorLayout />;
  };

  return (
    <AppWrapper>
      <Provider store={store}>
        <ThemeProvider theme={themes.main}>
          <DataProvider>
            <DataClientProviderBridge />
            <BrowserRouter future={{ v7_startTransition: true }}>
              <ErrorBoundary errorLayout={handleError}>
                <Blocker>
                  <Toasts />
                  <Suspense>
                    <PendoInitializer pendoAppId={EnvUtil.pendoAppId} />
                    <LightTheme backgroundName="none">
                      <Sidedrawer>
                        <ServiceWorkerProvider />
                        <PendoProvider />
                        <AppRouter />
                      </Sidedrawer>
                    </LightTheme>
                  </Suspense>
                </Blocker>
              </ErrorBoundary>
            </BrowserRouter>
          </DataProvider>
        </ThemeProvider>
      </Provider>
    </AppWrapper>
  );
};

const root = createRoot(document.querySelector('#root'));
root.render(<Bootstrap />);
